import { Component, DestroyRef, type OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Permission } from '@sds/permissions';
import { filter, map, merge, of } from 'rxjs';
import { MenuItem } from '../sidenav/menu-item/menu-item.model';
import { SidenavStore } from '../sidenav/ngrx/sidenav.store';

@Component({
  imports: [RouterModule],
  selector: 'sds-route-handler',
  templateUrl: './route-handler.component.html',
  styleUrl: './route-handler.component.scss'
})
export class RouteHandlerComponent implements OnInit {
  sidenavStore = inject(SidenavStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  ngOnInit(): void {
    const url$ = merge(
      of(this.router.url),
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects)
      )
    );

    url$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(url => {
      this.sidenavStore.setCurrentlyOpenByRoute(url);
    });
    this.createSidenavItems();
  }

  private createSidenavItems() {
    // Define Menu Items here
    // Top Level Item (The item to click on so the dropdown opens)
    const administration = new MenuItem({
      name: 'menu.administration.label',
      icon: 'sl:administration',
      route: '/administration',
      subItems: [
        new MenuItem({
          name: 'menu.managedFleets',
          icon: 'dashboard',
          requiredPermissions: [Permission.ManagedFleets],
          subItems: [],
          route: '/managed-fleets',
          id: 'managedFleets'
        }),
        new MenuItem({
          name: 'plantDetail.remoteConfiguration',
          icon: 'settings_remote',
          requiredPermissions: [Permission.PlantsRemoteConfig],
          subItems: [],
          route: '/remote-configuration',
          id: 'remote-configuration'
        }),
        new MenuItem({
          name: 'menu.administration.portals',
          route: '/administration/portals',
          icon: 'sl:portals',
          subItems: [],
          requiredPermissions: [Permission.PortalMenuRead],
          id: 'administration-portals'
        }),
        new MenuItem({
          name: 'menu.administration.fleets',
          route: '/administration/fleets',
          icon: 'sl:fleet-management',
          subItems: [],
          requiredPermissions: [Permission.FleetsRead],
          id: 'administration-fleets'
        }),
        new MenuItem({
          name: 'menu.administration.plants',
          route: '/administration/plants',
          icon: 'sl:plant-management',
          subItems: [],
          requiredPermissions: [Permission.PlantsRead],
          id: 'administration-plants'
        }),
        new MenuItem({
          name: 'menu.administration.users',
          route: '/administration/users',
          icon: 'sl:user-management',
          requiredPermissions: [Permission.UsersMenuRead],
          subItems: [],
          id: 'administration-users'
        }),
        new MenuItem({
          name: 'quotaStatistics.appName',
          icon: 'api',
          requiredPermissions: [Permission.QuotaSupport],
          subItems: [],
          route: '/quota-statistics',
          id: 'quota-statistics'
        }),
        new MenuItem({
          name: 'menu.automation',
          icon: 'sl:condition',
          requiredPermissions: [Permission.AutomationApp],
          subItems: [],
          route: '/automation',
          id: 'automation'
        })
      ]
    });

    const monitoring = new MenuItem({
      name: 'menu.monitoring.label',
      icon: 'sl:monitoring',
      requiredPermissions: [Permission.MonitoringRead],
      subItems: [],
      route: '/monitoring',
      id: 'monitoring'
    });

    const analysis = new MenuItem({
      name: 'menu.analysis',
      icon: 'insights',
      requiredPermissions: [Permission.AnalysisRead],
      subItems: [],
      route: '/analysis',
      id: 'analysis'
    });

    const dataStudio = new MenuItem({
      name: 'menu.dataStudio.dataStudio',
      icon: 'library_books',
      requiredPermissions: [Permission.DataStudioRead],
      subItems: [
        new MenuItem({
          name: 'menu.dataStudio.powerManagement',
          icon: 'bolt',
          requiredPermissions: [Permission.DataStudioPm],
          subItems: [],
          route: '/power-management',
          id: 'power-management'
        }),
        new MenuItem({
          name: 'menu.dataStudio.valueMatrix',
          icon: 'list_alt',
          requiredPermissions: [Permission.DataStudioMatrix],
          subItems: [],
          route: '/value-matrix',
          id: 'value-matrix'
        }),
        new MenuItem({
          name: 'menu.dataStudio.kpi',
          icon: 'insights',
          requiredPermissions: [Permission.DataStudioKpi],
          subItems: [],
          route: '/kpi',
          id: 'kpi'
        }),
        new MenuItem({
          name: 'menu.eventLog',
          icon: 'dynamic_form',
          requiredPermissions: [Permission.EventLogRead],
          subItems: [],
          route: '/event-log',
          id: 'event-log'
        })
      ],
      route: '/value-matrix'
    });

    const tasksList = new MenuItem({
      name: 'menu.taskList',
      icon: 'sl:task-list',
      requiredPermissions: [Permission.TaskListRead],
      subItems: [],
      route: '/task-list',
      id: 'task-list'
    });

    const presentation = new MenuItem({
      name: 'menu.presentation.label',
      icon: 'play_circle',
      route: '/presentation',
      subItems: [
        new MenuItem({
          name: 'menu.pinboards.label',
          icon: 'view_comfy',
          requiredPermissions: [Permission.PinboardRead],
          subItems: [],
          route: '/pinboard',
          id: 'pinboards'
        }),
        new MenuItem({
          name: 'menu.slideshow.label',
          icon: 'video_library',
          requiredPermissions: [Permission.SlideshowRead],
          subItems: [],
          route: '/slideshow',
          id: 'slideshows'
        })
      ]
    });

    const reports = new MenuItem({
      name: 'reports.label',
      icon: 'description',
      requiredPermissions: [Permission.ReportRead],
      subItems: [],
      route: '/reports',
      id: 'reports'
    });

    const logfileConfigurator = new MenuItem({
      name: 'menu.configurator',
      icon: 'code',
      requiredPermissions: [Permission.WebdriverRead],
      subItems: [],
      route: '/webdriver-configurator',
      id: 'webdriver-configurator'
    });

    this.sidenavStore.setSidenavItems([
      monitoring,
      analysis,
      dataStudio,
      tasksList,
      administration,
      presentation,
      reports,
      logfileConfigurator
    ]);
  }
}
